<template>
    <BaseModal
        :title="isEditing ? 'Edit Academic Year' : 'Add Academic Year'"
        :loading="loading"
        :submit-text="isEditing ? 'Update' : 'Create'"
        max-width="2xl"
        @close="$emit('close')"
        @submit="handleSubmit"
    >
        <form @submit.prevent="handleSubmit">
            <div class="space-y-6">
                <!-- Academic Year Information Section -->
                <div>
                    <div class="grid grid-cols-1 gap-4">
                        <!-- Name -->
                        <div>
                            <label
                                class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                            >
                                Academic Year Name *
                            </label>
                            <input
                                type="text"
                                v-model="formData.name"
                                required
                                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-colors"
                                :class="{
                                    'border-red-500': errors.name,
                                }"
                                placeholder="e.g., 2025/2026"
                            />
                            <p
                                v-if="errors.name"
                                class="mt-1 text-sm text-red-600 dark:text-red-400"
                            >
                                {{ errors.name }}
                            </p>
                        </div>

                        <!-- Start Date -->
                        <div>
                            <label
                                class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                            >
                                Start Date *
                            </label>
                            <input
                                type="date"
                                v-model="formData.start_date"
                                required
                                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-colors"
                                :class="{
                                    'border-red-500': errors.start_date,
                                }"
                            />
                            <p
                                v-if="errors.start_date"
                                class="mt-1 text-sm text-red-600 dark:text-red-400"
                            >
                                {{ errors.start_date }}
                            </p>
                        </div>

                        <!-- End Date -->
                        <div>
                            <label
                                class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                            >
                                End Date *
                            </label>
                            <input
                                type="date"
                                v-model="formData.end_date"
                                required
                                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-colors"
                                :class="{
                                    'border-red-500': errors.end_date,
                                }"
                            />
                            <p
                                v-if="errors.end_date"
                                class="mt-1 text-sm text-red-600 dark:text-red-400"
                            >
                                {{ errors.end_date }}
                            </p>
                        </div>

                        <!-- Is Active -->
                        <div
                            class="flex items-center space-x-3 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-md"
                        >
                            <input
                                type="checkbox"
                                v-model="formData.is_active"
                                id="is_active"
                                class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded transition-colors"
                            />
                            <label
                                for="is_active"
                                class="text-sm font-medium text-gray-900 dark:text-white cursor-pointer"
                            >
                                Set as active academic year
                            </label>
                            <p
                                class="text-xs text-gray-500 dark:text-gray-400 ml-auto"
                            >
                                Only one academic year can be active at a time
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </form>
    </BaseModal>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import BaseModal from "@/components/BaseModal.vue";
import type { AcademicYear, AcademicYearFormData } from "@/api/academicYear";

const props = defineProps<{
    academicYear?: AcademicYear | null;
    isEditing?: boolean;
}>();

import { useAcademicYearStore } from "@/stores/academicYear";

const emit = defineEmits<{
    close: [];
    saved: [];
}>();

const academicYearStore = useAcademicYearStore();
const loading = ref(false);
const errors = ref<Record<string, string>>({});
const formData = ref<AcademicYearFormData>({
    name: "",
    start_date: "",
    end_date: "",
    is_active: false,
});

// Computed property for isEditing
const isEditing = computed(() => props.isEditing || !!props.academicYear);

// Initialize form data when editing
watch(
    () => props.academicYear,
    () => {
        if (props.academicYear) {
            formData.value = {
                name: props.academicYear.name,
                start_date: props.academicYear.start_date?.split("T")[0] ?? "",
                end_date: props.academicYear.end_date?.split("T")[0] ?? "",
                is_active: props.academicYear.is_active,
            };
        } else {
            // Reset form for new academic year
            formData.value = {
                name: "",
                start_date: "",
                end_date: "",
                is_active: false,
            };
        }
        errors.value = {};
    },
    { immediate: true },
);

const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Name validation
    if (!formData.value.name.trim()) {
        newErrors.name = "Academic year name is required";
    }

    // Date validation
    if (!formData.value.start_date) {
        newErrors.start_date = "Start date is required";
    }

    if (!formData.value.end_date) {
        newErrors.end_date = "End date is required";
    }

    if (formData.value.start_date && formData.value.end_date) {
        const startDate = new Date(formData.value.start_date);
        const endDate = new Date(formData.value.end_date);

        if (startDate >= endDate) {
            newErrors.end_date = "End date must be after start date";
        }
    }

    errors.value = newErrors;
    return Object.keys(newErrors).length === 0;
};

const handleSubmit = async () => {
    if (!validateForm()) {
        return;
    }

    loading.value = true;
    errors.value = {};
    try {
        if (isEditing.value && props.academicYear) {
            await academicYearStore.updateAcademicYear(props.academicYear.id, {
                ...formData.value,
            });
        } else {
            await academicYearStore.createAcademicYear({ ...formData.value });
        }
        formData.value = {
            name: "",
            start_date: "",
            end_date: "",
            is_active: false,
        };
        errors.value = {};
        emit("saved");
    } catch (error: any) {
        if (error.response?.status === 422) {
            const apiErrors = error.response.data.errors;
            for (const [key, messages] of Object.entries(apiErrors)) {
                errors.value[key] = Array.isArray(messages)
                    ? (messages as string[])[0]
                    : String(messages);
            }
            return;
        }
        throw error;
    } finally {
        loading.value = false;
    }
};
</script>
